import { ObjectUtils } from '../main/ObjectUtils.js';

export const ChildVisitOrder = {
  childrenFirst: 0,
  childrenLast: 1,
  noChildren: 2,
};

export class Visitor {
  constructor() {
  }

  /** Called before we start visitation by calling guest.acceptVisitor(). */
  doBeforeVisit(guest) {
  }

  /** Called after we're done with visitation. */
  doAfterVisit(guest) {
  }

  visit(guest) {
    switch (this.childVisitOrder) {
      default:
      case ChildVisitOrder.childrenFirst:
        guest.visitChildren(this);
        return guest.acceptVisitor(this);
      case ChildVisitOrder.childrenLast: {
        const value = guest.acceptVisitor(this);
        guest.visitChildren(this);
        return value;
      }
      case ChildVisitOrder.noChildren:
        return guest.acceptVisitor(this);
    }
  }

  /** Main method called by user/owner when they want to visit all guests. */
  visitRoot(guest) {
    this.doBeforeVisit(guest);
    const value = this.visit(guest);
    this.doAfterVisit(guest);
    return value;
  }

  pushContext(data) {
    const context = {
      prior: this.currentContext,
      data: data,
    };
    return this.currentContext = context;
  }

  popContext() {
    const ctx = this.currentContext;
    this.currentContext = this.currentContext.prior;
    return ctx;
  }

  /**
   * Helper method which calls pushContext, then visitChildren on the parent, finally popContext.
   * @param {*} parent The parent of the child objects being visited.
   * @param {*} data User defined data, e.g. the parent html element.
   * @param {*} parentContext If defined the properties of this object will be assigned onto the
   * new currentContext.
   */
  visitChildren(parent, data, parentContext) {
    this.pushContext(data);
    if (parentContext) {
      ObjectUtils.assign(this.currentContext, parentContext);
    }
    parent.visitChildren(this);
    if (parentContext) {
      // Remove the properties we assigned:
      for (var property in parentContext) {
        delete this.currentContext[property];
      }
    }
    this.popContext();
  }
}

export class ProductVisitor extends Visitor {
  constructor() {
    super();
  }

  /** Called by product component instances letting us know they are being visited. */
  visitComponent(component) {
  }

  visitDoorPanel(component) {
  }

  visitDoorDivider(component) {
  }

  visitDoorCore(component) {
  }

  visitDoorRail(component) {
  }

  visitDoorStile(component) {
  }

  visitDoorFrame(component) {
  }

  visitDoor(component) {
  }

  visitDoorBottomTrack(component) {
  }

  visitDoorTopTrack(component) {
  }

  visitDoorFront(component) {
  }

  visitSideWall(component) {
  }

  visitFlexiSide(component) {
  }

  visitFlexiBasketAndTracks(component) {
  }

  visitMeshBasketAndTracks(component) {
  }

  visitSupportRodWithBrackets(component) {
  }

  visitHangRodWithBrackets(component) {
  }

  visitFlexiShelf(component) {
  }

  visitTopShelf(component) {
  }

  visitFlexiSectionContents(component) {
  }

  visitFlexiSection(component) {
  }

  visitHangRodModule(component) {
  }

  visitTopShelfModule(component) {
  }

  visitClosetOrganizer(component) {
  }
}

export const SizeStrategy = {
  none: 0,
  round: 1,
  ceil: 2,
}

export const DefaultSizeOptions = {
  width: {
    strategy: SizeStrategy.ceil,
    decimals: 1,
  },
  height: {
    strategy: SizeStrategy.ceil,
    decimals: 1,
  },
}

export const DoorSizeOptions = {
  width: {
    // Round door width to nearest whole percent to fix potential display bugs
    strategy: SizeStrategy.round,
    decimals: 0,
  },
  height: {
    strategy: SizeStrategy.ceil,
    decimals: 1,
  },
}

export const OrganizerSizeOptions = {
  width: {
    strategy: SizeStrategy.none,
  },
  height: {
    strategy: SizeStrategy.none,
  },
}

export class ProductDrawingVisitor extends ProductVisitor {
  constructor() {
    super();
  }

  clearDrawing() {
  }

  /** Called before base class starts visitation by calling component.acceptVisitor(). */
  doBeforeVisit(component) {
    super.doBeforeVisit(component);
    this.clearDrawing();
  }

  getRotation(component) {
    return (this.currentContext ? this.currentContext.rotation : null) ||
      (component ? component.rotation : null);
  }
}

export class ToggleLabelVisitor extends ProductVisitor {
  constructor(toggleOn) {
    super();
    // We want to manually control when/how children are visited:
    this.childVisitOrder = ChildVisitOrder.noChildren;
    this.toggleOn = toggleOn;
  }

  visitDoorPanel(panel) {
    this.toggleLabel(panel);
  }

  visitDoor(door) {
    door.core.visitChildren(this);
  }

  visitDoorFront(front) {
    front.visitChildren(this);
  }

  visitSideWall(sideWall) {
    this.toggleLabel(sideWall);
  }

  visitFlexiSide(flexiSide) {
    this.toggleLabel(flexiSide);
  }

  visitFlexiBasketAndTracks(basket) {
    this.toggleLabel(basket);
  }

  visitMeshBasketAndTracks(basket) {
    this.toggleLabel(basket);
  }

  visitSupportRodWithBrackets(supportRod) {
    // Don't think we want to display labels on the rods
  }

  visitHangRodWithBrackets(hangRod) {
    // Don't think we want to display labels on the rods
  }

  visitTopShelf(topShelf) {
    this.toggleLabel(topShelf);
  }

  visitFlexiShelf(flexiShelf) {
    this.toggleLabel(flexiShelf);
  }

  visitFlexiSectionContents(contents) {
    contents.visitChildren(this);
  }

  visitFlexiSection(flexiSection) {
    flexiSection.visitChildren(this);
  }

  visitHangRodModule(module) {
    module.visitChildren(this);
  }

  visitTopShelfModule(module) {
    module.visitChildren(this);
  }

  visitClosetOrganizer(organizer) {
    organizer.visitChildren(this);
  }

  toggleLabel(component) {
    // Should be overridden by subclasses
  }
}

export class ToggleInteractivityVisitor extends ProductVisitor {
  constructor(onToggleInteractive) {
    super();
    // We want to manually control when/how children are visited:
    this.childVisitOrder = ChildVisitOrder.noChildren;
    this.onToggleInteractive = onToggleInteractive;
  }

  visitDoorPanel(panel) {
    this.onToggleInteractive(panel);
  }

  visitDoor(door) {
    door.core.visitChildren(this);
  }

  visitDoorFront(front) {
    front.visitChildren(this);
  }

  visitSideWall(sideWall) {
    this.toggleInteractivity(sideWall);
  }

  visitFlexiSide(flexiSide) {
    this.toggleInteractivity(flexiSide);
  }

  visitFlexiBasketAndTracks(basket) {
    this.toggleInteractivity(basket);
  }

  visitMeshBasketAndTracks(basket) {
    this.toggleInteractivity(basket);
  }

  visitSupportRodWithBrackets(hangRod) {
    this.toggleInteractivity(hangRod);
  }

  visitHangRodWithBrackets(supportRod) {
    this.toggleInteractivity(supportRod);
  }

  visitTopShelf(topShelf) {
    this.toggleInteractivity(topShelf);
  }

  visitFlexiShelf(flexiShelf) {
    this.toggleInteractivity(flexiShelf);
  }

  visitFlexiSection(flexiSection) {
    flexiSection.visitChildren(this);
  }

  visitFlexiSectionContents(contents) {
    contents.visitChildren(this);
  }
}
